import React, { useState } from 'react';
import { router } from '@inertiajs/react';
import { TripsLayout } from '../../components/layout/TripsLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Search, Users, UserCheck, Crown, LogIn } from 'lucide-react';

interface User {
  id: string;
  eid: string;
  email: string;
  firstname: string;
  lastname: string;
  name: string;
  avatar_image_url: string;
  avatar_fallback: string;
  role: 'standard' | 'admin';
  manager_id?: string;
  manager_name?: string;
  department?: string;
  travel_counts: {
    pending: number;
    approved: number;
    rejected: number;
  };
}

interface PeopleIndexProps {
  users: User[];
  user: {
    id: string;
    is_admin: boolean;
  };
  routes: {
    impersonations_create: string;
  };
}

export default function Index({ users, user, routes }: PeopleIndexProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'standard' | 'admin'>('all');

  // Filter users based on search term and role
  const filteredUsers = users.filter(user => {
    const displayName = `${user.firstname} ${user.lastname}`.trim() || user.name;
    const matchesSearch = searchTerm === '' ||
      displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRole = roleFilter === 'all' || user.role === roleFilter;

    return matchesSearch && matchesRole;
  });


  const getRoleBadgeVariant = (role: string) => {
    return role === 'admin' ? 'default' : 'outline';
  };

  const getRoleIcon = (role: string) => {
    return role === 'admin' ? <Crown className="h-3 w-3" /> : <UserCheck className="h-3 w-3" />;
  };

  const handleImpersonate = (userId: string) => {
    router.post(routes.impersonations_create, {
      user_id: userId
    });
  };

  return (
    <TripsLayout>
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Users className="h-6 w-6" />
            <div>
              <CardTitle className="text-2xl">People Directory</CardTitle>
              <CardDescription>
                View all team members and their information
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4 pointer-events-none z-10" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant={roleFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setRoleFilter('all')}
              >
                All ({users.length})
              </Button>
              <Button
                variant={roleFilter === 'admin' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setRoleFilter('admin')}
              >
                Admins ({users.filter(u => u.role === 'admin').length})
              </Button>
              <Button
                variant={roleFilter === 'standard' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setRoleFilter('standard')}
              >
                Standard ({users.filter(u => u.role === 'standard').length})
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12"></TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="hidden sm:table-cell">Email</TableHead>
                <TableHead className="text-center">Role</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Manager</TableHead>
                <TableHead className="text-center">Requests (P/A/R)</TableHead>
                {user.is_admin && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={user.is_admin ? 8 : 7} className="text-center py-8 text-muted-foreground">
                    {searchTerm ? 'No people found matching your search.' : 'No people found.'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((currentUser) => (
                  <TableRow key={currentUser.id} className="hover:bg-muted/50">
                    <TableCell>
                      <Avatar className="h-8 w-8 sm:h-10 sm:w-10">
                        <AvatarImage src={currentUser.avatar_image_url} alt={`${currentUser.firstname} ${currentUser.lastname}`.trim() || currentUser.name} />
                        <AvatarFallback>{currentUser.avatar_fallback}</AvatarFallback>
                      </Avatar>
                    </TableCell>
                    <TableCell className="font-medium min-w-0">
                      <div className="truncate">
                        {`${currentUser.firstname} ${currentUser.lastname}`.trim() || currentUser.name}
                      </div>
                      <div className="text-xs text-muted-foreground sm:hidden truncate">
                        {currentUser.email}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground hidden sm:table-cell min-w-0">
                      <div className="truncate">{currentUser.email}</div>
                    </TableCell>
                    <TableCell className="text-center align-middle">
                      <Badge variant={getRoleBadgeVariant(currentUser.role)} className="inline-flex items-center gap-1 text-xs">
                        {getRoleIcon(currentUser.role)}
                        <span className="hidden sm:inline">{currentUser.role.charAt(0).toUpperCase() + currentUser.role.slice(1)}</span>
                        <span className="sm:hidden">{currentUser.role === 'admin' ? 'A' : 'S'}</span>
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground min-w-0">
                      <div className="truncate">{currentUser.department || '—'}</div>
                    </TableCell>
                    <TableCell className="text-muted-foreground min-w-0">
                      <div className="truncate">{currentUser.manager_name || '—'}</div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="text-xs font-mono">
                        {currentUser.travel_counts.pending} / {currentUser.travel_counts.approved} / {currentUser.travel_counts.rejected}
                      </Badge>
                    </TableCell>
                    {user.is_admin && (
                      <TableCell className="text-right">
                        <div className="ml-auto flex gap-1 w-fit">
                          <Button
                            variant="default"
                            size="sm"
                            className="h-8 px-2"
                            onClick={() => handleImpersonate(currentUser.id)}
                          >
                            <LogIn className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {filteredUsers.length > 0 && (
        <div className="mt-4 text-sm text-muted-foreground text-center">
          Showing {filteredUsers.length} of {users.length} people
        </div>
      )}
    </TripsLayout>
  );
}
